import { Typography, Form, Button, message, Spin } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import VerificationInput from "react-verification-input";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getLoggedUserInfo, type UserState } from "redux-context/user";
import { postSignupCode, sendVerificationCode } from "services/SauceService";
import { ReactComponent as Logo } from "assets/images/logo.svg";
import "./Verification.scss";
import { BusinessState, getBusinessess, getBusinessInfo } from "redux-context/business";
import { getDomainMetadata } from "redux-context/domain";
import {
  trackVerificationConfirmClicked,
  trackVerificationResendClicked,
  trackVerificationSuccess,
  trackVerifyEmailPageViewed,
} from "analytics";

enum VerificationType {
  EMAIL = "email",
}

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;
const SENT_EMAIL_KEY = "verification_sent_email";

function markEmailAsSent(email?: string) {
  if (typeof email === "string") {
    try {
      localStorage.setItem(SENT_EMAIL_KEY, email);
    } catch (e) {
      // ignore localStorage failures
    }
  }
  return localStorage.getItem(SENT_EMAIL_KEY);
}

const VerificationCode: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const dispatch = useDispatch<any>();

  const [sending, setSending] = useState<boolean>(false);
  const [verifying, setVerifying] = useState<boolean>(false);
  const [code, setCode] = useState<string>("");

  const [resendCooldown, setResendCooldown] = useState<number>(0);

  const [searchParams] = useSearchParams();
  const { loggedUser } = useSelector((state: UserState) => state.user);
  const { business, subscription } = useSelector(
    (state: BusinessState) => state.business,
  );
  const hasTrackedVerifyPageViewedRef = useRef(false);
  const businessId = (business as any)?._id ?? null;
  const planType = (subscription as any)?.planName ?? null;
  const currency =
    (subscription as any)?.stripeDetails?.nextPaymentDetails?.currency ?? null;

  // URLSearchParams may decode '+' into a space (application/x-www-form-urlencoded behavior).
  // Restore '+' characters in the extracted email so addresses like 'test+something@...' keep the plus.
  const rawEmail = searchParams.get("email") || undefined;
  const emailFromQuery = rawEmail ? rawEmail.replace(/ /g, "+") : undefined;

  const businessName = searchParams.get("businessName") || undefined;

  const isCodeComplete = useMemo(
    () => (code || "").replace(/\D/g, "").length === CODE_LENGTH,
    [code],
  );

  const analyticsContext = useMemo(
    () => ({
      pathname: location.pathname,
      search: location.search,
      businessId,
      userId: loggedUser?.id ?? null,
      userEmail: loggedUser?.email ?? null,
      planType,
      currency,
    }),
    [
      businessId,
      currency,
      location.pathname,
      location.search,
      loggedUser?.email,
      loggedUser?.id,
      planType,
    ],
  );

  useEffect(() => {
    form.resetFields();
  }, [form, loggedUser]);

  useEffect(() => {
    if (hasTrackedVerifyPageViewedRef.current) {
      return;
    }

    hasTrackedVerifyPageViewedRef.current = true;
    trackVerifyEmailPageViewed(analyticsContext);
  }, [analyticsContext]);

  // Cooldown ticker
  useEffect(() => {
    if (resendCooldown <= 0) return;

    const id = window.setInterval(() => {
      setResendCooldown((v) => Math.max(0, v - 1));
    }, 1000);

    return () => window.clearInterval(id);
  }, [resendCooldown]);

  useEffect(() => {
    const sendIfNeeded = async () => {
      if (!emailFromQuery) {
        message.error("Email not provided in query string.");
        return;
      }

      // If we've already recorded that a code was sent to this exact email,
      // avoid sending automatically again on refresh. If the recorded email is
      // different (user changed email), allow sending to the new email at least once.
      const existingSentEmail = localStorage.getItem(SENT_EMAIL_KEY);
      if (existingSentEmail && existingSentEmail === emailFromQuery) {
        return;
      }

      setSending(true);
      try {
        await sendVerificationCode({
          type: VerificationType.EMAIL,
          contact: emailFromQuery,
        });
        // Record the email we successfully sent a code to so a refresh won't auto-resend to the same address.
        markEmailAsSent(emailFromQuery);
        setResendCooldown(RESEND_COOLDOWN_SECONDS);

        message.success("Verification code sent.");
      } catch (err) {
        // If send fails, remove recorded sent email so future attempts or refreshes can retry.
        localStorage.removeItem(SENT_EMAIL_KEY);
        message.error("Error sending verification code.");
      } finally {
        setSending(false);
      }
    };

    sendIfNeeded();
  }, [emailFromQuery]);

  const onClickVerify = async (value?: string) => {
    const verificationCode = (
      code ||
      form.getFieldValue("verificationCode") ||
      "" ||
      value
    )
      .toString()
      .replace(/\D/g, "");

    if (verificationCode.length !== CODE_LENGTH) {
      message.error(`Please enter the ${CODE_LENGTH}-digit code.`);
      return;
    }

    if (!emailFromQuery) {
      message.error("No email provided.");
      return;
    }

    trackVerificationConfirmClicked(analyticsContext);

    const params = {
      email: emailFromQuery,
      code: verificationCode,
      businessName: businessName,
    };

    setVerifying(true);
    try {
      const response: any = await postSignupCode(params);

      if (response && response.accessToken) {
        localStorage.setItem("token", response.accessToken);
        if (response.businessId)
          localStorage.setItem("businessId", response.businessId);

        dispatch(getBusinessess());
        dispatch(getBusinessInfo());
        dispatch(getDomainMetadata());
        dispatch(getLoggedUserInfo());
        trackVerificationSuccess(analyticsContext);
        navigate("/");
        return;
      }

      message.error("Invalid code. Please try again.");
    } catch (err) {
      message.error("Error verifying the code. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const resendCode = async () => {
    if (!emailFromQuery) {
      message.error("No email to resend to.");
      return;
    }

    if (resendCooldown > 0) return;

    trackVerificationResendClicked(analyticsContext);
    setSending(true);
    try {
      await sendVerificationCode({
        type: VerificationType.EMAIL,
        contact: emailFromQuery,
      });

      // Ensure we record the email that received the code so subsequent refreshes
      // don't auto-trigger another send to the same address.
      try {
        markEmailAsSent(emailFromQuery);
      } catch (e) {
        // ignore localStorage failures
      }

      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      message.success("Code resent.");
    } catch (err) {
      message.error("Error resending verification code.");
    }
    setSending(false);
  };

  const onChangeEmail = () => {
    // Safer default: go back to the previous step (landing/signup form).
    navigate(-1);
  };

  return (
    <div className="verification-page">
      <Spin spinning={sending}>
        <div className="verification-card">
          <Logo className="verification-logo" />

          <div className="verification-header">
            <Typography.Title level={3} className="verification-title">
              Verify your email
            </Typography.Title>

            <Typography.Text className="verification-subtitle">
              We sent a {CODE_LENGTH}-digit code to{" "}
              <span className="verification-email">
                {emailFromQuery || "your email"}
              </span>
              .
            </Typography.Text>

            <Typography.Text className="verification-helper">
              Enter the code below to confirm your email address.
            </Typography.Text>
          </div>

          <Form
            form={form}
            layout="vertical"
            className="verification-form"
            onFinish={onClickVerify}
          >
            <div className="verification-controls">
              <div className="code-box">
                <Form.Item
                  label="Verification code"
                  name="verificationCode"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the code!",
                    },
                  ]}
                >
                  <div
                    className="verification-code-inputs"
                    role="group"
                    aria-label="verification code"
                  >
                    <VerificationInput
                      length={CODE_LENGTH}
                      validChars="0-9"
                      inputProps={{
                        inputMode: "numeric",
                        autoComplete: "one-time-code",
                        "aria-label": "verification digit",
                      }}
                      value={code}
                      onChange={(val: string) => setCode(val)}
                      onComplete={(value) => {
                        onClickVerify(value);
                      }}
                    />
                  </div>
                </Form.Item>
              </div>

              <Button
                type="primary"
                className="verification-button"
                htmlType="submit"
                loading={verifying}
                disabled={!isCodeComplete || verifying}
                style={{ width: "100%" }}
              >
                Confirm email
              </Button>

              <div className="verification-secondary">
                <button
                  type="button"
                  className="resend-link"
                  onClick={resendCode}
                  disabled={resendCooldown > 0 || sending}
                >
                  {resendCooldown > 0
                    ? `Resend in ${resendCooldown}s`
                    : "Resend code"}
                </button>

                <span
                  className="secondary-divider"
                  aria-hidden="true"
                  style={{
                    display: "none",
                  }}
                >
                  •
                </span>

                <button
                  type="button"
                  className="change-email-link"
                  onClick={onChangeEmail}
                  disabled={sending || verifying}
                  style={{
                    display: "none",
                  }}
                >
                  Change email
                </button>
              </div>

              <Typography.Text className="verification-tip">
                Tip: check your spam/junk folder if you don’t see the email.
              </Typography.Text>
            </div>
          </Form>
        </div>
      </Spin>
    </div>
  );
};

export default VerificationCode;
